import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';

export interface PublishState {
  ready: boolean;
  projectKind: string;
  checks: Array<{ name: string; ok: boolean; hint?: string }>;
  activeVersion: number | null;
  activeSlug: string | null;
  publishedAt: string | null;   // ISO datetime
  publicUrl: string | null;     // absolute URL — external if available
  externalUrl: string | null;   // when the last publish went to Cloudflare/Fly
  activeTarget: string | null;  // "cloudflare_pages" | "fly_io" | "nexus_hosted"
  loading: boolean;
  publishing: boolean;
  availableTargets: Array<{ id: string; label: string; available: boolean; hint?: string }>;
  refresh: () => Promise<void>;
  publish: (target?: string) => Promise<{
    url: string;
    version: number;
    target: string;
    externalUrl: string | null;
    fallbackReason?: string | null;
  } | null>;
  rollback: (version: number) => Promise<boolean>;
}

/**
 * `usePublishStatus` — single source of truth for the publish state of
 * a workspace. Consumed by both the header status pill (`PublishStatusBar`)
 * and the deploy drawer, so the two panels can never contradict each
 * other (which was the root cause of the state-sync bug in v6).
 *
 * Behaviour:
 *  - Loads readiness + history on mount.
 *  - Polls readiness every 10 s while the workspace is open (so the
 *    button flips green the moment the SPA build lands on disk).
 *  - After a successful `publish()`, immediately refreshes both pieces
 *    so the pill shows "🟢 En vivo v2" without waiting for the next poll.
 */
export function usePublishStatus(workspaceId: string): PublishState {
  const [ready, setReady] = useState(false);
  const [projectKind, setProjectKind] = useState('unknown');
  const [checks, setChecks] = useState<PublishState['checks']>([]);
  const [activeVersion, setActiveVersion] = useState<number | null>(null);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [externalUrl, setExternalUrl] = useState<string | null>(null);
  const [activeTarget, setActiveTarget] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [availableTargets, setAvailableTargets] = useState<PublishState['availableTargets']>([]);
  const mounted = useRef(true);

  const buildPublicUrl = useCallback((slug: string) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}/api/public/${slug}/`;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [rd, hist, tgs] = await Promise.all([
        apiService.publishReadiness(workspaceId),
        apiService.publishHistory(workspaceId).catch(() => ({ versions: [] })),
        apiService.publishTargets(workspaceId).catch(() => ({ targets: [] })),
      ]);
      if (!mounted.current) return;
      setReady(rd.ready);
      setProjectKind(rd.project_kind || 'unknown');
      setChecks(rd.checks || []);
      setAvailableTargets(tgs.targets || []);
      const active = (hist.versions || []).find((v: any) => v.active);
      if (active) {
        setActiveVersion(active.version);
        setActiveSlug(active.slug);
        setPublishedAt(active.published_at);
        setActiveTarget(active.target || null);
        const ext = active?.external?.url || null;
        setExternalUrl(ext);
        setPublicUrl(ext || buildPublicUrl(active.slug));
      } else {
        setActiveVersion(null);
        setActiveSlug(null);
        setPublishedAt(null);
        setPublicUrl(null);
        setExternalUrl(null);
        setActiveTarget(null);
      }
    } catch {
      /* silent — polling will retry */
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [workspaceId, buildPublicUrl]);

  const publish = useCallback(async (target: string = 'auto') => {
    setPublishing(true);
    try {
      const r = await apiService.publishWorkspace(workspaceId, target);
      await refresh();
      const url = r.external_url || buildPublicUrl(r.slug);
      return {
        url,
        version: r.version,
        target: r.target,
        externalUrl: r.external_url || null,
        fallbackReason: r.external?.fallback_reason || null,
      };
    } catch {
      return null;
    } finally {
      setPublishing(false);
    }
  }, [workspaceId, refresh, buildPublicUrl]);

  const rollback = useCallback(async (version: number) => {
    try {
      await apiService.publishRollback(workspaceId, version);
      await refresh();
      return true;
    } catch {
      return false;
    }
  }, [workspaceId, refresh]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const t = setInterval(refresh, 10000);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, [refresh]);

  return {
    ready, projectKind, checks,
    activeVersion, activeSlug, publishedAt, publicUrl,
    externalUrl, activeTarget,
    loading, publishing, availableTargets,
    refresh, publish, rollback,
  };
}
